// Ayarlar > Genel'deki güvenlik alanları (madde 11 ve 12) — uygulamadan bağımsız.
// İki bileşen de fragment döndürür: projenin kendi sütun/form düzenine olduğu gibi
// konur. Değerler kontrollüdür (state ve kaydetme projenin ayar formunda);
// telefon kutusu projenin kendi bileşeniyle `phoneInput` olarak verilir.

import type { ReactNode } from "react";

export type TwoFactorMode = "OFF" | "OPTIONAL" | "REQUIRED_ADMINS" | "REQUIRED_STAFF";

export const TWO_FACTOR_MODES: readonly TwoFactorMode[] = ["OFF", "OPTIONAL", "REQUIRED_ADMINS", "REQUIRED_STAFF"];

/** Sunucudan gelen değeri güvenle moda çevirir (bilinmeyen → OFF). */
export function toTwoFactorMode(value: unknown): TwoFactorMode {
  return TWO_FACTOR_MODES.find((m) => m === value) ?? "OFF";
}

export interface SettingsFieldClassNames {
  title?: string;
  hint?: string;
  checkbox?: string;
  button?: string;
}

const DEFAULT_CLASSES: Required<SettingsFieldClassNames> = {
  title: "form-section-title",
  hint: "hint",
  checkbox: "checkbox-label",
  button: "btn-secondary",
};

export interface TwoFactorPolicyStrings {
  title: string;
  hint: string;
  policyLabel: string;
  modes: Record<TwoFactorMode, string>;
  smsLabel: string;
  adminOnly: string;
}

export const DEFAULT_TWO_FACTOR_POLICY_STRINGS: TwoFactorPolicyStrings = {
  title: "İki Adımlı Doğrulama (2FA)",
  hint:
    "Girişte şifreye ek olarak doğrulama uygulamasından (ya da SMS ile) gelen kod istenir. Zorunlu modda kurmamış kullanıcılar bir sonraki girişte kurulum ekranına yönlendirilir. Kullanıcı telefonunu kaybederse Kullanıcılar ekranından 2FA'sını sıfırlayabilirsiniz.",
  policyLabel: "Politika",
  modes: {
    OFF: "Kapalı",
    OPTIONAL: "İsteğe bağlı (her kullanıcı kendi açar)",
    REQUIRED_ADMINS: "Admin ve Yöneticiler için zorunlu",
    REQUIRED_STAFF: "Tüm personel için zorunlu",
  },
  smsLabel: "SMS ile kod yöntemine izin ver (NetGSM OTP şifresi gerekir)",
  adminOnly: "Bu ayarı yalnızca admin kullanıcısı değiştirebilir.",
};

export interface TwoFactorPolicyFieldsProps {
  mode: TwoFactorMode;
  onModeChange: (mode: TwoFactorMode) => void;
  smsEnabled: boolean;
  onSmsEnabledChange: (enabled: boolean) => void;
  /** false ise "SMS ile kod" onay kutusu hiç gösterilmez — SMS altyapısı olmayan uygulamalar için (varsayılan true). */
  showSms?: boolean;
  /** false ise alanlar yerine `adminOnly` metni gösterilir (yalnızca ADMIN değiştirir). */
  editable: boolean;
  strings?: Partial<TwoFactorPolicyStrings>;
  classNames?: SettingsFieldClassNames;
}

/** 2FA politikası: başlık + açıklama + "Politika" açılır kutusu + "SMS ile kod" onay kutusu ( ile gizlenir). */
export function TwoFactorPolicyFields({
  mode,
  onModeChange,
  smsEnabled,
  onSmsEnabledChange,
  showSms = true,
  editable,
  strings,
  classNames,
}: TwoFactorPolicyFieldsProps) {
  const s = { ...DEFAULT_TWO_FACTOR_POLICY_STRINGS, ...strings };
  const modes = { ...DEFAULT_TWO_FACTOR_POLICY_STRINGS.modes, ...strings?.modes };
  const c = { ...DEFAULT_CLASSES, ...classNames };
  return (
    <>
      <div className={c.title}>{s.title}</div>
      <p className={c.hint}>{s.hint}</p>
      {editable ? (
        <>
          <label>
            {s.policyLabel}
            <select value={mode} onChange={(e) => onModeChange(toTwoFactorMode(e.target.value))}>
              {TWO_FACTOR_MODES.map((m) => (
                <option key={m} value={m}>
                  {modes[m]}
                </option>
              ))}
            </select>
          </label>
          {showSms && (
            <label className={c.checkbox}>
              <input type="checkbox" checked={smsEnabled} onChange={(e) => onSmsEnabledChange(e.target.checked)} />
              {s.smsLabel}
            </label>
          )}
        </>
      ) : (
        <p className={c.hint}>{s.adminOnly}</p>
      )}
    </>
  );
}

export interface SecurityAlertStrings {
  title: string;
  hint: string;
  phoneLabel: string;
  testButton: string;
  adminOnly: string;
}

export const DEFAULT_SECURITY_ALERT_STRINGS: SecurityAlertStrings = {
  title: "Güvenlik Bildirimleri",
  hint:
    "Art arda hatalı giriş denemesiyle kilitlenen hesaplar, yönetici hesabına daha önce görülmemiş bir IP adresinden giriş, başarısız otomatik güncelleme ve bağımlılık taramasında bulunan açıklar bu numaraya SMS ile bildirilir (NetGSM OTP/bilgilendirme hesabı kullanılır). Boş bırakılırsa SMS gönderilmez, olaylar yalnızca kullanıcı işlemlerine kaydedilir.",
  phoneLabel: "Güvenlik bildirim telefonu",
  testButton: "Test Bildirimi Gönder",
  adminOnly: "Bu ayarı yalnızca admin kullanıcısı değiştirebilir.",
};

export interface SecurityAlertFieldsProps {
  /** Projenin kendi telefon kutusu (ör. `<PhoneInput value onChange clearable />`). */
  phoneInput: ReactNode;
  /** Test düğmesi: proje önce numarayı kaydedip test ucunu çağırır, sonucu kendisi gösterir. */
  onTest: () => void;
  /** Düğme yalnızca numara doluyken ve test sürmüyorken etkin olmalı. */
  testDisabled: boolean;
  editable: boolean;
  strings?: Partial<SecurityAlertStrings>;
  classNames?: SettingsFieldClassNames;
}

/** Güvenlik bildirimleri: başlık + açıklama + telefon + "Test Bildirimi Gönder". */
export function SecurityAlertFields({
  phoneInput,
  onTest,
  testDisabled,
  editable,
  strings,
  classNames,
}: SecurityAlertFieldsProps) {
  const s = { ...DEFAULT_SECURITY_ALERT_STRINGS, ...strings };
  const c = { ...DEFAULT_CLASSES, ...classNames };
  return (
    <>
      <div className={c.title}>{s.title}</div>
      <p className={c.hint}>{s.hint}</p>
      {editable ? (
        <>
          <label>
            {s.phoneLabel}
            {phoneInput}
          </label>
          <div>
            <button type="button" className={c.button} onClick={onTest} disabled={testDisabled}>
              {s.testButton}
            </button>
          </div>
        </>
      ) : (
        <p className={c.hint}>{s.adminOnly}</p>
      )}
    </>
  );
}

export type LoginPuzzleMode = "OFF" | "SUSPICIOUS" | "ALWAYS";

export const LOGIN_PUZZLE_MODES: readonly LoginPuzzleMode[] = ["OFF", "SUSPICIOUS", "ALWAYS"];

/** Sunucudan gelen değeri güvenle moda çevirir (bilinmeyen → SUSPICIOUS). */
export function toLoginPuzzleMode(value: unknown): LoginPuzzleMode {
  return LOGIN_PUZZLE_MODES.find((m) => m === value) ?? "SUSPICIOUS";
}

export interface LoginPuzzlePolicyStrings {
  title: string;
  hint: string;
  policyLabel: string;
  modes: Record<LoginPuzzleMode, string>;
  adminOnly: string;
}

export const DEFAULT_LOGIN_PUZZLE_POLICY_STRINGS: LoginPuzzlePolicyStrings = {
  title: "Giriş Yapbozu",
  hint:
    "Girişte parçayı kaydırarak boşluğa yerleştirme doğrulaması. \"Şüpheli durumda\" seçiliyken yalnızca aynı adresten hatalı denemeler ya da bot şüphesi olduğunda gösterilir; normal kullanıcı görmez.",
  policyLabel: "Ne zaman gösterilsin",
  modes: {
    OFF: "Kapalı",
    SUSPICIOUS: "Şüpheli durumda (önerilen)",
    ALWAYS: "Her girişte",
  },
  adminOnly: "Bu ayarı yalnızca admin kullanıcısı değiştirebilir.",
};

export interface LoginPuzzlePolicyFieldsProps {
  mode: LoginPuzzleMode;
  onModeChange: (mode: LoginPuzzleMode) => void;
  editable: boolean;
  strings?: Partial<LoginPuzzlePolicyStrings>;
  classNames?: SettingsFieldClassNames;
}

/** Giriş yapbozu politikası: başlık + açıklama + açılır kutu (Kapalı / Şüpheli durumda / Her girişte). */
export function LoginPuzzlePolicyFields({ mode, onModeChange, editable, strings, classNames }: LoginPuzzlePolicyFieldsProps) {
  const s = { ...DEFAULT_LOGIN_PUZZLE_POLICY_STRINGS, ...strings };
  const modes = { ...DEFAULT_LOGIN_PUZZLE_POLICY_STRINGS.modes, ...strings?.modes };
  const c = { ...DEFAULT_CLASSES, ...classNames };
  return (
    <>
      <div className={c.title}>{s.title}</div>
      <p className={c.hint}>{s.hint}</p>
      {editable ? (
        <label>
          {s.policyLabel}
          <select value={mode} onChange={(e) => onModeChange(toLoginPuzzleMode(e.target.value))}>
            {LOGIN_PUZZLE_MODES.map((m) => (
              <option key={m} value={m}>
                {modes[m]}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className={c.hint}>{s.adminOnly}</p>
      )}
    </>
  );
}
